import { useState, useEffect } from 'react';
import RoleCard from '../../elements/role_card';
import RoleList from '../../elements/role_list';
import {Link} from 'react-router-dom';
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import Loading from '../../cors/Loading';
import { toast } from 'sonner';

const countPermissions = (role) => {
  return Object.keys(role).filter(
    (key) =>
      key !== 'id' &&
      key !== 'name' &&
      key !== 'created_at' &&
      key !== 'updated_at' &&
      typeof role[key] === 'boolean' &&
      role[key] === true
  ).length;
};

export default function RolesPage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRoles = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(BASE_URL + '/api/role', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`API Error: ${response.status} - ${errorText}`);
          throw new Error(`Failed to fetch roles: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.code && Array.isArray(data.data)) {
          setRoles(data.data);
        } else if (Array.isArray(data)) {
          setRoles(data);
        } else {
          console.error('API response for roles is not an array:', data);
          setError(t('roles_page.update_data_format_error'));
          setRoles([]);
          toast.error(t('roles_page.update_api_format_error'));
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching roles:', err);
        toast.error(err.message || t('roles_page.update_loading_error'));
      } finally {
        setLoading(false);
      }
    };
    
  useEffect(() => {
    fetchRoles();
  }, [token]);


  const filteredRoles = roles
    .filter((role) => {
      // Simple search filter - just by name
      return role.name.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'permissions':
          return countPermissions(b) - countPermissions(a);
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('roles_page.title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('roles_page.subtitle')}
            </p>
          </div>
          <div className="mt-6 md:mt-0">
            <Link to="/roles/create">
              <button className="cursor-pointer text-xs bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold py-2.5 px-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                <span className="flex items-center">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t('roles_page.add_new')}
                </span>
              </button>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-4 mb-5">
          <div className="flex flex-col xl:flex-row gap-6">
            <div className="relative flex-1 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('roles_page.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              <div className="relative mr-2">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex dark:bg-stone-900 cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg
                    className="h-4 w-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs dark:text-gray-300">{t('roles_page.filter')}</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${
                      showSortDropdown ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs">
                      {[
                        { value: 'name', label: t('roles_page.sort_name') },
                        { value: 'newest', label: t('roles_page.sort_newest') },
                        { value: 'oldest', label: t('roles_page.sort_oldest') },
                        { value: 'permissions', label: t('roles_page.sort_permissions') },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value
                              ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex bg-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 cursor-pointer transition-all duration-200 ${
                    viewMode === 'grid'
                      ? 'bg-[var(--color-primary-500)] text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 cursor-pointer transition-all duration-200 ${
                    viewMode === 'list'
                      ? 'bg-[var(--color-primary-500)] text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading and error feedback */}
        {loading && (
          <div className="flex justify-center text-[var(--color-primary-500)] text-5xl mt-40">
            <Loading />
          </div>
        )}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            <div className="mb-2">
              <span className="inline-flex items-center  px-3 py-1 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px]">
                {filteredRoles.length} {t(filteredRoles.length === 1 ? 'roles_page.role_found_singular' : 'roles_page.role_found_plural')}
              </span>
            </div>

            {filteredRoles.length > 0 ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredRoles.map((role) => (
                    <RoleCard key={role.id} role={role} fetchRoles={fetchRoles} />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRoles.map((role) => (
                    <RoleList key={role.id} role={role} fetchRoles={fetchRoles} />
                  ))}
                </div>
              )
            ) : (
              <div className="text-center">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg
                    className="h-12 w-12 text-[var(--color-primary-500)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('roles_page.no_roles_found')}</h3>
                <p className="text-gray-600 text-sm mb-6">
                  {t('roles_page.no_roles_desc')}
                </p>
                <Link to="/roles/create">
                  <button className="bg-[var(--color-primary-500)] cursor-pointer text-xs hover:bg-[var(--color-primary-400)] text-white font-semibold py-2.5 px-4 rounded-2xl transition-all duration-300">
                    {t('roles_page.create_first')}
                  </button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}